import { getAddress, isAddress } from 'viem';
import { z } from 'zod';

import {
  analyticsWindowSchema,
  servedMessageSchema,
  type AnalyticsWindow
} from './schemas';

/**
 * What every `/api/v1/investor/:address/...` read on Portfolio, Overview and
 * Risk & liquidity shares: the scope echo a response must carry, the
 * windows a read is asked for, and the envelope of the ledger aggregates
 * with its warnings. The scope check and the address normaliser are
 * `assertAnalyticsResponseScope` and `normalizeAnalyticsAddress` in
 * `./schemas`.
 * Shared here because those reads are owned by more than one feature.
 */

const nonNegativeNumber = z.number().finite().nonnegative();
const isoDate = z.union([z.iso.date(), z.iso.datetime({ offset: true })]);
const nullableIsoDate = isoDate.nullable();
const addressSchema = z
  .string()
  .refine(isAddress, 'Invalid EVM address')
  .transform((value) => getAddress(value));

export const investorScopeSchema = z.object({
  ownerAddress: addressSchema,
  chainId: z.number().int().positive()
});

/**
 * The windows an investor read is asked for: the same list `/risk` takes
 * (`analyticsWindowSchema`), named for the reads that use it here.
 */
export const investorMetricWindowSchema = analyticsWindowSchema;
export type InvestorMetricWindow = AnalyticsWindow;

/**
 * Ledger warnings are served as { code, message } objects (or bare strings).
 * The text is never shown, so a long one is cut, never refused.
 * `ROWA_POSITIONS_TRUNCATED` carries `{ included, totalPositions }` in
 * `context` — kept so the UI can say how far the traversal got (the old
 * 100-row cap is gone since v1.31.0; the safety ceiling is 10,000).
 */
export const ledgerWarningSchema = z.union([
  z.object({
    code: z.string().max(128),
    message: servedMessageSchema,
    context: z.record(z.string(), z.unknown()).optional()
  }),
  // A bare string is code and text in one; screens read only its code.
  servedMessageSchema
]);

export const investorAggregateEnvelopeSchema = investorScopeSchema.extend({
  asOf: z.iso.datetime({ offset: true }),
  window: z.string().max(32),
  windowFrom: nullableIsoDate,
  windowTo: nullableIsoDate,
  windowDays: nonNegativeNumber.int(),
  populationScope: z.string().max(64),
  totalPositions: nonNegativeNumber.int(),
  warnings: z.array(ledgerWarningSchema).max(100)
});
